import {getSharedAudioContext} from "./audioUnlock"

export interface CompressVideoOptions {
  maxWidth: number
  maxHeight: number
  videoBitsPerSecond: number
  audioBitsPerSecond?: number
  frameRate?: number
  onProgress?: (percent: number) => void
}

export interface CompressVideoResult {
  blob: Blob
  mimeType: string
  extension: string
  width: number
  height: number
  /** True if the source appeared to contain an audio track. */
  sourceHadAudio: boolean
  /** True if an audio track was successfully attached to the compressed output. */
  audioAttached: boolean
}

const MIME_CANDIDATES: Array<{mime: string, extension: string}> = [
  {mime: 'video/mp4;codecs=avc1.42E01E,mp4a.40.2', extension: 'mp4'},
  {mime: 'video/mp4;codecs=avc1.42E01E', extension: 'mp4'},
  {mime: 'video/mp4;codecs=avc1', extension: 'mp4'},
  {mime: 'video/mp4', extension: 'mp4'},
  {mime: 'video/webm;codecs=vp9,opus', extension: 'webm'},
  {mime: 'video/webm;codecs=vp8,opus', extension: 'webm'},
  {mime: 'video/webm;codecs=vp9', extension: 'webm'},
  {mime: 'video/webm;codecs=vp8', extension: 'webm'},
  {mime: 'video/webm', extension: 'webm'}
]

// If playback makes no progress for this long we assume the pipeline has
// stalled (a known iOS failure mode) and bail out so the caller can upload the
// original instead of hanging the whole queue forever.
const STALL_TIMEOUT_MS = 15_000

const pickMimeType = (): {mime: string, extension: string} | null => {
  if (typeof MediaRecorder === 'undefined') return null
  for (const c of MIME_CANDIDATES) {
    try {
      if (MediaRecorder.isTypeSupported(c.mime)) return c
    } catch {
    }
  }
  return null
}

export const isCompressVideoSupported = (): boolean => pickMimeType() !== null

export async function compressVideo(
  file: File | Blob,
  options: CompressVideoOptions
): Promise<CompressVideoResult> {
  const picked = pickMimeType()
  if (!picked) throw new Error('MediaRecorder not supported by this browser')

  const url = URL.createObjectURL(file)
  const video = document.createElement('video')
  video.muted = true
  video.playsInline = true
  video.preload = 'auto'
  // iOS Safari will not reliably decode/play a <video> that is detached from
  // the document, so mount it off-screen for the duration of the transcode.
  video.style.position = 'fixed'
  video.style.left = '-9999px'
  video.style.top = '0'
  video.style.width = '1px'
  video.style.height = '1px'
  video.style.opacity = '0'
  video.style.pointerEvents = 'none'
  video.src = url
  document.body.appendChild(video)

  const teardown: Array<() => void> = []
  teardown.push(() => { try { URL.revokeObjectURL(url) } catch {} })
  teardown.push(() => { try { video.pause() } catch {} })
  teardown.push(() => { try { video.remove() } catch {} })
  const cleanup = () => { while (teardown.length) teardown.pop()!() }

  try {
    await new Promise<void>((resolve, reject) => {
      const onLoaded = () => { video.removeEventListener('error', onError); resolve() }
      const onError = () => { video.removeEventListener('loadedmetadata', onLoaded); reject(new Error('Failed to load video metadata')) }
      video.addEventListener('loadedmetadata', onLoaded, {once: true})
      video.addEventListener('error', onError, {once: true})
    })

    const srcW = video.videoWidth
    const srcH = video.videoHeight
    if (!srcW || !srcH) throw new Error('Video has no dimensions')

    const aspect = srcW / srcH
    let outW = srcW
    let outH = srcH
    if (outW > options.maxWidth) {
      outW = options.maxWidth
      outH = Math.round(outW / aspect)
    }
    if (outH > options.maxHeight) {
      outH = options.maxHeight
      outW = Math.round(outH * aspect)
    }
    outW = Math.max(2, Math.floor(outW / 2) * 2)
    outH = Math.max(2, Math.floor(outH / 2) * 2)

    const canvas = document.createElement('canvas')
    canvas.width = outW
    canvas.height = outH
    const ctx = canvas.getContext('2d', {alpha: false})
    if (!ctx) throw new Error('Could not acquire 2D context')

    const fps = options.frameRate ?? 30
    const canvasStream = (canvas as any).captureStream
      ? (canvas as any).captureStream(fps) as MediaStream
      : null
    if (!canvasStream) throw new Error('canvas.captureStream not supported')

    const combined = new MediaStream()
    canvasStream.getVideoTracks().forEach(t => combined.addTrack(t))

    // Route the element's audio into the recording via the shared, gesture-
    // unlocked AudioContext. We only do this when the context is actually
    // "running" — routing a media element through a suspended context is what
    // stalls playback on iOS, so if it isn't unlocked we skip audio entirely
    // (the video still plays and finishes) and let the caller decide.
    let audioAttached = false
    const audioContext = getSharedAudioContext()
    if (audioContext && audioContext.state === 'running') {
      try {
        const sourceNode = audioContext.createMediaElementSource(video)
        const dest = audioContext.createMediaStreamDestination()
        sourceNode.connect(dest)
        const audioTracks = dest.stream.getAudioTracks()
        audioTracks.forEach(t => combined.addTrack(t))
        audioAttached = audioTracks.length > 0
        // Never close the shared context — just release this element's nodes.
        teardown.push(() => { try { sourceNode.disconnect() } catch {} })
        teardown.push(() => { try { dest.disconnect() } catch {} })
      } catch {
        audioAttached = false
      }
    }

    const recorder = new MediaRecorder(combined, {
      mimeType: picked.mime,
      videoBitsPerSecond: options.videoBitsPerSecond,
      audioBitsPerSecond: options.audioBitsPerSecond ?? 128_000
    })
    teardown.push(() => { try { if (recorder.state !== 'inactive') recorder.stop() } catch {} })

    const chunks: Blob[] = []
    recorder.ondataavailable = e => { if (e.data && e.data.size > 0) chunks.push(e.data) }

    const stopped = new Promise<void>((resolve, reject) => {
      recorder.onstop = () => resolve()
      recorder.onerror = (e: any) => reject(e?.error ?? new Error('MediaRecorder error'))
    })

    let frameHandle: number | null = null
    let drawing = true
    const draw = () => {
      if (!drawing) return
      try {
        ctx.drawImage(video, 0, 0, outW, outH)
      } catch {
      }
      if (options.onProgress && video.duration && isFinite(video.duration)) {
        const pct = Math.min(100, Math.round((video.currentTime / video.duration) * 100))
        options.onProgress(pct)
      }
      frameHandle = requestAnimationFrame(draw)
    }

    // Resolve when playback reaches the end. A plain 'ended' listener is not
    // enough on iOS — the event sometimes never fires — so we also poll
    // currentTime against duration and enforce a stall timeout. Whatever frames
    // were captured up to that point still produce a valid (if shorter) clip.
    const finishedPlaying = new Promise<void>((resolve, reject) => {
      let settled = false
      let lastTime = 0
      let lastAdvance = Date.now()

      const finish = () => {
        if (settled) return
        settled = true
        clearInterval(watchdog)
        resolve()
      }
      const abort = (err: Error) => {
        if (settled) return
        settled = true
        clearInterval(watchdog)
        reject(err)
      }

      video.addEventListener('ended', finish, {once: true})
      video.addEventListener('error', () => abort(new Error('Video playback error')), {once: true})

      const watchdog = setInterval(() => {
        const now = Date.now()
        if (video.currentTime > lastTime + 0.01) {
          lastTime = video.currentTime
          lastAdvance = now
        }
        const duration = video.duration
        if (isFinite(duration) && duration > 0 && video.currentTime >= duration - 0.25) {
          finish()
        } else if (now - lastAdvance > STALL_TIMEOUT_MS) {
          abort(new Error('Video playback stalled'))
        }
      }, 500)
    })

    // A muted element feeds silence into the Web Audio graph, so once its audio
    // is routed into the recording we have to unmute it — otherwise the output
    // gets an audio track that contains nothing but silence. This makes nothing
    // audible: createMediaElementSource redirects the element's output into the
    // graph, and we only connect it to the recording destination.
    if (audioAttached) {
      video.muted = false
      video.volume = 1
    }

    recorder.start(1000)
    // Browsers that refuse unmuted playback outside a user gesture (typically
    // iOS Safari) reject here; the rejection propagates so the caller uploads
    // the original file, sound included.
    await video.play()
    frameHandle = requestAnimationFrame(draw)

    await finishedPlaying
    drawing = false
    if (frameHandle != null) cancelAnimationFrame(frameHandle)

    // Best-effort detection of whether the source actually contained audio.
    // Evaluated after playback so webkitAudioDecodedByteCount is populated.
    const v = video as any
    const sourceHadAudio =
      typeof v.mozHasAudio === 'boolean' ? v.mozHasAudio
      : typeof v.webkitAudioDecodedByteCount === 'number' ? v.webkitAudioDecodedByteCount > 0
      : typeof v.audioTracks?.length === 'number' ? v.audioTracks.length > 0
      : audioAttached

    if (recorder.state !== 'inactive') recorder.stop()
    await stopped

    const blob = new Blob(chunks, {type: picked.mime})
    if (!blob.size) throw new Error('Compressed video is empty')
    cleanup()
    options.onProgress?.(100)

    return {blob, mimeType: picked.mime.split(';')[0], extension: picked.extension, width: outW, height: outH, sourceHadAudio, audioAttached}
  } catch (err) {
    cleanup()
    throw err
  }
}
